// src/components/ProdMgrs/ProdMgrsQuotaBreakdownTable.jsx

import React from 'react';
import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import { useInterval } from '@tzmedical/react-hooks';

import axios from '../../../axiosClient.js';
import Alert from '../../common/Alert.jsx';
import PageLoading from '../../common/PageLoading.jsx';
import ProdMgrsQuotaBreakdownHeader from './ProdMgrsQuotaBreakdownHeader.jsx';
import ProdMgrsQuotaBreakdownRow from './ProdMgrsQuotaBreakdownRow.jsx';

const REFRESH_INTERVAL_MS = 300_000;

function ProdMgrsQuotaBreakdownTable({ selectedRep, selectedYear }) {
  const [alertError, setAlertError] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [quotaBreakdown, setQuotaBreakdown] = React.useState([]);

  const fetchQuotaBreakdown = React.useCallback(async () => {
    try {
      const { data } = await axios({
        method: 'GET',
        url: '/api/prod-mgrs/quota-breakdown',
        params: { rep: selectedRep, year: selectedYear },
      });
      setQuotaBreakdown(Array.isArray(data) ? data : []);
      setAlertError(null);
    } catch (err) {
      setAlertError(err.response?.data?.error || err.message);
    }
    setLoading(false);
  }, [selectedRep, selectedYear]);

  React.useEffect(() => {
    setLoading(true);
    setAlertError(null);
    setQuotaBreakdown([]);
  }, [selectedRep, selectedYear]);

  useInterval(fetchQuotaBreakdown, REFRESH_INTERVAL_MS, loading && !alertError);

  return (
    <Box>
      <Typography data-cy="pmgr-quota-breakdown-header" variant="h5" sx={{ textAlign: 'center' }}>
        Quota Breakdown
      </Typography>
      <Alert
        data-cy="pmgr-quota-breakdown-alert"
        message={alertError}
        setMessage={setAlertError}
        level="error"
      />
      {loading ? (
        <PageLoading />
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <ProdMgrsQuotaBreakdownHeader />
            <TableBody>
              {quotaBreakdown.length ? (
                quotaBreakdown.map((quota) => (
                  <ProdMgrsQuotaBreakdownRow key={quota.CATEGORY} quota={quota} />
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={11} style={{ textAlign: 'center', padding: '1rem' }}>
                    No quota data set for {selectedRep}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

ProdMgrsQuotaBreakdownTable.propTypes = {
  selectedRep: PropTypes.string.isRequired,
  selectedYear: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};

export default React.memo(ProdMgrsQuotaBreakdownTable);
